import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Providers, MsalProvider } from "@microsoft/mgt-element";
import { Buffer } from 'buffer/';
import {
  Tooltip,
  Button,
} from "@fluentui/react-components";
import { DocumentRegular, DocumentFilled, ShareRegular } from "@fluentui/react-icons";
import { SearchResults } from '@microsoft/mgt-react';
import { pages } from "@microsoft/teams-js";

function Viewer(props) {

  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false); // State to toggle visibility of search results
  const [selectedData, setSelectedData] = useState(null);
  const [isViewerInitialized, setIsViewerInitialized] = useState(false);
  const [selectedBcfTopic, setSelectedBcfTopic] = useState(null);
  const lastAppliedDeepLinkTopicGuidRef = useRef(null);

  // New state to store BCF file information
  const [bcfFiles, setBcfFiles] = useState([]); // Array of { id, url, name, driveId }

  async function ensurePermission(scopes) {
    const credential = await Providers.globalProvider._credential;
    try {
      await credential.getToken(scopes);
      return true;
    } catch (error) {
      try {
        await credential.login(scopes);
        return true;
      } catch (err) {
        return false;
      }
    }
  }

  // Function to toggle the visibility of search results
  const toggleSearchResults = () => {
    setShowSearchResults(prev => !prev);
  };

  const shareTabDeepLink = async () => {
    const normalizedSelectedTopicGuid = normalizeTopicGuid(selectedBcfTopic?.guid);
    const normalizedSelectedTopicTitle = normalizeTopicTitle(selectedBcfTopic?.title);
    const deepLinkParameters = {
      subPageId: buildDeepLinkSubPageId(normalizedSelectedTopicGuid),
      subPageLabel: normalizedSelectedTopicTitle || "IFC Viewer",
    };

    try {
      await pages.shareDeepLink(deepLinkParameters);
    } catch (error) {
      console.error("Unable to share Teams deep link.", error);
      try {
        await navigator.clipboard.writeText(window.location.href);
        console.info("Copied current URL to clipboard as a fallback.");
      } catch (clipboardError) {
        console.error("Unable to copy URL to clipboard fallback.", clipboardError);
      }
    }
  };

  useEffect(() => {
    fetchSearchResults();
  }, [showSearchResults]);

  useEffect(() => {
    fetchSearchResults();
  }, [selectedData]);

  const fetchSearchResults = () => {
    if (selectedData && showSearchResults) {
      const attributes = extractAttributesUnderNameAttributes(selectedData);
      const searchQueryResult = buildSearchQuery(attributes, props.teamSiteUrl);
      console.log(searchQueryResult)
      setSearchQuery(searchQueryResult)
    }
  }

  function urlToSharingToken(url) {
    var trimEnd = function (str, c) {
      c = c ? c : ' ';
      var i = str.length - 1;
      for (; i >= 0 && str.charAt(i) == c; i--);
      return str.substring(0, i + 1);
    };
    var value = Buffer.from(url).toString('base64');
    return "u!" + trimEnd(value, '=').replace(/\//g, '_').replace(/\+/g, '-');
  }

  const fetchIfcFile = async (url) => {
    const client = Providers.globalProvider.graph.client;
    try {
      const sharedItemId = urlToSharingToken(url);
      const metadataUrl = "/shares/" + sharedItemId + "/driveItem";
      const metadataResponse = await client.api(metadataUrl).get();
      const filename = metadataResponse.name.replace(/\.[^/.]+$/, "");


      const requestUrl = "/shares/" + sharedItemId + "/driveitem/content";
      const responseBlob = await client.api(requestUrl).responseType('raw').get();
      const arrayBuffer = await responseBlob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      if (metadataResponse.name.endsWith('.bcf') || metadataResponse.name.endsWith('.bcfzip')) {
        // Load BCF
        window.dispatchEvent(new CustomEvent("bcfLoadEvent", {
          detail: {
            name: "openBCF",
            payload: { name: filename, buffer: uint8Array },
          },
        }));

        // Save BCF file information to state
        setBcfFiles(prev => [
          ...prev,
          {
            id: metadataResponse.id,
            driveId: metadataResponse.parentReference.driveId,
            url: metadataResponse.webUrl,
            name: metadataResponse.name,
          }
        ]);
      } else if (metadataResponse.name.endsWith('.ifc')) {
        window.dispatchEvent(new CustomEvent("ifcLoadEvent", {
          detail: {
            name: "openModel",
            payload: { name: filename, buffer: uint8Array },
          },
        }));
      }

    } catch (error) {
      console.error(`Error fetching file ${url}`, error);
    }
  };

  useEffect(() => {
    let ifcViewerElement = null;
    const handleIfcSelectionEvent = (event) => {
      setSelectedData(event?.detail?.selectedValue[0]);
    };
    const handleBcfSelectionEvent = (event) => {
      const topic = extractTopicFromBcfEventDetail(event?.detail);
      if (topic) {
        setSelectedBcfTopic(topic);
      }
    };

    async function fetchIfcFileSequentially(urls) {
      const client = Providers.globalProvider.graph.client;
      for (const rawUrl of urls) {
        let url = decodeURIComponent(rawUrl);
        if (url.includes('/Lists/')) {
          console.warn(`Skipping Microsoft List URL: ${rawUrl}`);
          continue;
        }
        let meta;
        try {
          const sharingToken = urlToSharingToken(url);
          meta = await client.api(`/shares/${sharingToken}/driveItem`).get();
        } catch (e) {
          console.warn(`Skipping invalid URL (not a valid SharePoint share): ${url}`, e);
          continue;
        }
        if (meta.folder) {
          let children;
          try {
            children = await client
              .api(`/drives/${meta.parentReference.driveId}/items/${meta.id}/children`)
              .get();
          } catch (e) {
            console.warn(`Failed to list folder contents, skipping: ${url}`, e);
            continue;
          }
          const fileItems = children.value.filter(item =>
            /\.(ifc|bcf|bcfzip)$/i.test(item.name)
          );
          for (const child of fileItems) {
            await fetchIfcFile(child.webUrl);
          }
        } else {
          await fetchIfcFile(url);
        }
      }
    }

    const waitForViewerInitialization = async () => {
      while (typeof window.initializeViewer !== 'function') {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    };

    const loadIFCViewer = async () => {
      try {
        await waitForViewerInitialization();
        await window.initializeViewer();
        setIsViewerInitialized(true)
      } catch (error) {
        console.error('Error loading IFCViewer:', error);
      }
    };

    async function fetchData() {
      try {
        await loadIFCViewer();
        window.microsoftTeams?.appInitialization.notifySuccess();

        if (props.urls) {
          await fetchIfcFileSequentially(props.urls);
        }

        ifcViewerElement = document.getElementById("ifc-viewer");
        if (ifcViewerElement) {
          ifcViewerElement.addEventListener("ifcSelectionEvent", handleIfcSelectionEvent);
          ifcViewerElement.addEventListener("bcfSelectionEvent", handleBcfSelectionEvent);
        } else {
          console.error("Element with id 'ifc-viewer' not found.");
        }

      } catch (error) {
        console.error('Error in fetchData:', error);
      }
    }

    fetchData();

    return () => {
      if (ifcViewerElement) {
        ifcViewerElement.removeEventListener("ifcSelectionEvent", handleIfcSelectionEvent);
        ifcViewerElement.removeEventListener("bcfSelectionEvent", handleBcfSelectionEvent);
      }
      if (window.clearModels) window.clearModels();
    };
  }, [props.auth]);

  useEffect(() => {
    if (!isViewerInitialized) {
      return;
    }

    const topicGuidToSelect = normalizeTopicGuid(props.deepLinkTopicGuid);
    if (!topicGuidToSelect) {
      return;
    }

    if (lastAppliedDeepLinkTopicGuidRef.current === topicGuidToSelect) {
      return;
    }

    const ifcViewerElement = document.getElementById("ifc-viewer");
    if (!ifcViewerElement) {
      console.error("Element with id 'ifc-viewer' not found.");
      return;
    }

    lastAppliedDeepLinkTopicGuidRef.current = topicGuidToSelect;
    ifcViewerElement.dispatchEvent(new CustomEvent("bcfselectionevent", {
      detail: { guid: topicGuidToSelect },
    }));
    ifcViewerElement.dispatchEvent(new CustomEvent("bcfSelectionEvent", {
      detail: { guid: topicGuidToSelect },
    }));
  }, [isViewerInitialized, props.deepLinkTopicGuid]);

  useEffect(() => {
    if (isViewerInitialized) {
      window.exportToExcel(async (blob, filename) => {
        if (!props.hasPremiumAccess) {
          return null;
        }

        const client = Providers.globalProvider.graph.client;
        const hasPermission = await ensurePermission(["Files.ReadWrite.All"]);
        if (!hasPermission) {
          throw Error;
        }

        try {
          const driveItem = await searchFilesStartingWith(filename.replace(".xlsx", ""));
          if (!driveItem) {
            if (!props.teamSiteUrl || !props.relativeUrl) {
              console.error("props.teamSiteUrl or props.relativeUrl not found");
              throw Error;
            }

            const url = new URL(props.teamSiteUrl);
            const sitePath = url.pathname;
            const folderUrl = props.teamSiteUrl.replace(sitePath, "") + props.relativeUrl;
            const sharingToken = urlToSharingToken(folderUrl);
            const folderResponse = await client.api(`/shares/${sharingToken}/driveItem`).get();
            const driveId = folderResponse.parentReference.driveId;
            const folderId = folderResponse.id;
            const uploadUrl = `/drives/${driveId}/items/${folderId}:/${filename}:/content`;
            const uploadResponse = await client.api(uploadUrl).put(blob);

            console.log(`File uploaded successfully to folder as ${filename}`);
            return uploadResponse.webUrl;
          }

          const driveId = driveItem.parentReference.driveId;
          const itemId = driveItem.id;
          const uploadUrl = `/drives/${driveId}/items/${itemId}/content`;
          const uploadResponse = await client.api(uploadUrl).put(blob);

          console.log(`File uploaded successfully to ${driveItem.webUrl}`);
          return uploadResponse.webUrl;
        } catch (error) {
          console.error(`Error uploading file:`, error);
          throw Error;
        }
      });
    }

  }, [props.teamSiteUrl, props.relativeUrl, isViewerInitialized, props.hasPremiumAccess]);

  useEffect(() => {
    if (!isViewerInitialized) return;

    window.exportBCF(async (bcfData, filename, topics) => {
      if (!props.hasPremiumAccess) {
        console.error("No premium license. Cannot export BCF.");
        throw new Error("No premium license. Cannot export BCF.");
      }

      const client = Providers.globalProvider.graph.client;
      const hasPermission = await ensurePermission(["Files.ReadWrite.All", "Sites.ReadWrite.All"]);
      if (!hasPermission) {
        console.error("Insufficient permissions to upload BCF or write to Lists.");
        throw new Error("Insufficient permissions to upload BCF or write to Lists.");
      }

      let updatedTopics = topics;

      // Update the List with topics if a valid list URL exists and there are topics
      if (props.urls && topics && topics.length > 0) {
        const listUrlCandidate = props.urls.find(u => u.includes("/Lists/"));
        if (listUrlCandidate) {
          try {
            updatedTopics = await updateListTopics(client, listUrlCandidate, topics);
          } catch (error) {
            throw new Error("Failed to update Microsoft List, " + error.message, error);
          }
        }
      }

      // Update the BCF file
      try {
        const bcfData = await window.generateBCFData(updatedTopics);
        // Find the matching BCF file by comparing filenames without extension
        const currentBcfFile = bcfFiles.find(file =>
          file.name.replace(/\.[^/.]+$/, "") === filename
        );
        if (!currentBcfFile) {
          throw new Error(`No matching BCF file found for ${filename}`);
        }
        await updateBCFFile(client, currentBcfFile, bcfData, filename);
        console.log(`Updated BCF file ${filename} successfully.`);
      } catch (error) {
        throw new Error("Failed to update BCF file. Make sure you have linked a file and have the necessary permissions.", error);
      }

      return null;
    });
  }, [isViewerInitialized, props.hasPremiumAccess, bcfFiles, props.urls]);


  const updateBCFFile = async (client, bcfFile, bcfData, filename) => {
    const blob = new Blob([bcfData], { type: 'application/octet-stream' });
    const uploadUrl = `/drives/${bcfFile.driveId}/items/${bcfFile.id}/content`;
    await client.api(uploadUrl).put(blob);
    return bcfFile.url;
  };

  const updateListTopics = async (client, listUrl, topics) => {

    // Extract hostname, site path, and list name from the provided URL.
    const urlObj = new URL(listUrl);
    const hostname = urlObj.hostname;
    const pathParts = urlObj.pathname.split('/').filter(Boolean);
    let sitePath = "";
    if (pathParts.includes("sites")) {
      const index = pathParts.indexOf("sites");
      sitePath = `/sites/${pathParts[index + 1]}`;
    } else if (pathParts.includes("teams")) {
      const index = pathParts.indexOf("teams");
      sitePath = `/teams/${pathParts[index + 1]}`;
    }
    const listNameIndex = pathParts.indexOf("Lists");
    const listName = listNameIndex > -1 ? pathParts[listNameIndex + 1] : null;

    if (!sitePath || !listName) {
      console.error("Invalid list URL:", listUrl);
      return;
    }

    // Get the site and list details from Microsoft Graph.
    const siteResponse = await client.api(`/sites/${hostname}:${sitePath}`).get();
    const siteId = siteResponse.id;
    const listResponse = await client.api(`/sites/${siteId}/lists/${listName}`).get();
    const listId = listResponse.id;

    // Fetch all list items (up to 1000)
    let existingItems = [];
    try {
      const itemsResponse = await client.api(`/sites/${siteId}/lists/${listId}/items?top=1000&expand=columns,items(expand=fields)`).get();
      existingItems = itemsResponse.value;
    } catch (err) {
      console.error("Error fetching list items:", err);
      throw new Error("Error fetching list items:", err);
    }

    // Step 1: Fetch all columns from the list.
    const columnsResponse = await client
      .api(`/sites/${siteId}/lists/${listId}/columns`)
      .get();
    const columns = columnsResponse.value || [];

    // Build a map: displayName -> internal name
    const displayToInternal = columns.reduce((map, col) => {
      map[col.displayName] = col.name;
      return map;
    }, {});

    const guidFieldName = displayToInternal["Guid"] || "Guid";

    // Map of topic property to SharePoint column display name
    const columnMappings = [
      { topicKey: "description", spDisplayName: "Description" },
      { topicKey: "status", spDisplayName: "Status" },
      { topicKey: "assignedTo", spDisplayName: "AssignedTo" },
      { topicKey: "dueDate", spDisplayName: "DueDate" },
      { topicKey: "type", spDisplayName: "TopicType" },
      { topicKey: "priority", spDisplayName: "Priority" },
      { topicKey: "creationDate", spDisplayName: "CreationDate" },
      { topicKey: "creationAuthor", spDisplayName: "CreationAuthor" },
      { topicKey: "modifiedDate", spDisplayName: "ModifiedDate" },
      { topicKey: "modifiedAuthor", spDisplayName: "ModifiedAuthor" },
      { topicKey: "stage", spDisplayName: "Stage" }
    ];

    // Iterate through topics.
    for (const topic of topics) {
      // Build the minimal fields object
      const listItem = {
        fields: {
          Title: topic.title,
          [displayToInternal["Guid"] || "Guid"]: topic.guid || null
        }
      };

      // Now for each mapping, see if Graph returned that displayName
      for (const { topicKey, spDisplayName } of columnMappings) {
        const internalName = displayToInternal[spDisplayName];
        if (!internalName) continue;

        let value = topic[topicKey] ?? null;
        // For date fields, ensure ISO string w/o millis
        if (value && ["dueDate", "creationDate", "modifiedDate"].includes(topicKey)) {
          value = new Date(new Date(value).setMilliseconds(0)).toISOString();
        }
        listItem.fields[internalName] = value;
      }

      // Update based on the GUID
      const existingItem = existingItems.find(item => item.fields && item.fields[guidFieldName] === topic.guid);

      if (existingItem) {
        // Compare all fields that are present in listItem.fields
        const fieldsToCompare = Object.keys(listItem.fields);
        let hasChanges = false;
        const existingFields = existingItem.fields || {};
        for (const key of fieldsToCompare) {
          if (existingFields[key] != listItem.fields[key]) {
            // Special handling for date fields
            if (
              (key === "DueDate" || key === "CreationDate" || key === "ModifiedDate") &&
              existingFields[key] &&
              listItem.fields[key] &&
              new Date(existingFields[key]).toISOString() === new Date(listItem.fields[key]).toISOString()
            ) {
              continue;
            }
            hasChanges = true;
            break;
          }
        }
        if (!hasChanges) {
          continue;
        }
        try {
          await client
            .api(`/sites/${siteId}/lists/${listId}/items/${existingItem.id}`)
            .update(listItem);
          console.log(`Updated topic with GUID ${topic.guid} in Microsoft List`);
        } catch (err) {
          console.error("Error updating BCF topic in Microsoft List:", err);
          throw new Error(err.message, err);
        }
      } else {
        try {
          const addItemResponse = await client
            .api(`/sites/${siteId}/lists/${listId}/items`)
            .post(listItem);
          console.log(`Added topic with GUID ${topic.guid} to Microsoft List`);
        } catch (err) {
          console.error("Error adding BCF topic to Microsoft List:", err);
          throw new Error(err.message, err);
        }
      }
    }
    return topics;
  };


  async function searchFilesStartingWith(prefix) {
    const client = Providers.globalProvider.graph.client;
    try {
      const searchBody = {
        requests: [
          {
            entityTypes: ["driveItem"],
            query: {
              queryString: `title:${prefix}*`
            },
            from: 0,
            size: 1,
            fields: ["name", "webUrl", "id", "parentReference"]
          }
        ]
      };
      const response = await client.api('/search/query').post(searchBody);
      const hitsContainers = response.value[0].hitsContainers;
      if (hitsContainers && hitsContainers.length > 0) {
        const hits = hitsContainers[0].hits;
        if (hits && hits.length > 0) {
          const item = hits[0].resource;
          return item;
        }
      }
      return null;
    } catch (error) {
      console.error("Error searching files:", error);
      return null;
    }
  }

  return (
    <div className="viewer-container" >
      {/* The viewer grid */}
      <bim-grid id="ifc-viewer" style={{ flex: 1 }}></bim-grid>

      <div className="viewer-action-buttons">
        <Tooltip content="Share this tab as a Teams deep link" relationship="label">
          <Button shape="circular" className="share-deep-link" onClick={shareTabDeepLink} size="large" icon={<ShareRegular />}>
          </Button>
        </Tooltip>
        <Tooltip content={(showSearchResults ? "Hide" : "Show") + " related documents for current selection"} relationship="label">
          <Button shape="circular" className="toggle-search-results" onClick={toggleSearchResults} size="large" icon={showSearchResults ? <DocumentFilled /> : <DocumentRegular />}>
          </Button>
        </Tooltip>
      </div>

      {showSearchResults && (
        <SearchResults entityTypes={['driveItem']} className="search-results" queryString={searchQuery}></SearchResults>
      )}

    </div>
  );
}

export default Viewer;

function extractAttributesUnderNameAttributes(data) {
  const attributesNode = data.children.find(child => child.data && child.data.Name === "Attributes");
  if (!attributesNode || !attributesNode.children) {
    return {};
  }
  const attributes = {};
  attributesNode.children.forEach(attr => {
    if (attr.data && attr.data.Name && attr.data.Value !== undefined) {
      attributes[attr.data.Name] = attr.data.Value.toString().trim();
    }
  });
  return attributes;
}

function buildSearchQuery(attributes, site) {
  const queryParts = [];
  if (site) {
    const escapedSite = site.replace(/([\\"])/g, '\\$1');
    queryParts.push(`Path:"${escapedSite}*"`);
  }

  const attributeQueryParts = [];
  for (const [key, value] of Object.entries(attributes)) {
    const escapedValue = value.replace(/([\\"])/g, '\\$1');
    attributeQueryParts.push(`"${escapedValue}"`);
  }

  const attributeQuery = attributeQueryParts.join(' OR ');
  const searchQuery = `(${queryParts.join(' AND ')}) AND (${attributeQuery})`;
  if (site)
    return searchQuery;
  else
    return attributeQuery;
}

function normalizeTopicGuid(guid) {
  if (typeof guid !== "string") {
    return null;
  }

  const trimmedGuid = guid.trim();
  return trimmedGuid || null;
}

function normalizeTopicTitle(title) {
  if (typeof title !== "string") {
    return null;
  }

  const trimmedTitle = title.trim();
  return trimmedTitle || null;
}

function buildDeepLinkSubPageId(topicGuid) {
  const normalizedGuid = normalizeTopicGuid(topicGuid);
  if (!normalizedGuid) {
    return "ifc-viewer";
  }

  return `ifc-viewer?topic_guid=${encodeURIComponent(normalizedGuid)}`;
}

function extractTopicFromBcfEventDetail(detail) {
  if (!detail || typeof detail !== "object") {
    return null;
  }

  const normalizedGuid = normalizeTopicGuid(detail.guid);
  if (!normalizedGuid) {
    return null;
  }

  const normalizedTitle = normalizeTopicTitle(detail.title);
  return normalizedTitle
    ? { guid: normalizedGuid, title: normalizedTitle }
    : { guid: normalizedGuid };
}
